import React, {
  useEffect,
  useMemo,
  useState,
  useCallback
} from "react";

import {
  Box,
  TextField,
  InputAdornment,
  Snackbar,
  Alert
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import AppButton from "../ui/AppButton";
import PageHeader from "../ui/PageHeader";
import DeleteDialog from "../modals/DeleteDialog";
import ProjectTable from "../components/projects/ProjectTable";
import ProjectDialog from "../components/projects/ProjectDialog";

import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  publishProject
} from "../services/projectService";
import { useUserRole } from "../context/UserRoleContext";
import { canWritePage } from "../auth/roles";

// Draft projects live here until they're published — once published they move
// to the main Projects page and assigned employees are notified. Visibility of
// drafts (creator-only, except super_admin) is enforced server-side.
function ProjectPlanning() {
  const { role } = useUserRole();

  const canManageProjects = canWritePage(role, "project_planning");

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProjects("", null, false, "Draft");
      setProjects(data);
    }
    catch (error) {
      console.error("Project Planning Load Error:", error);
      setSnackbar({
        open: true,
        message: "Failed to load draft projects",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      p.project_name?.toLowerCase().includes(q) ||
      String(p.project_id).includes(q)
    );
  }, [projects, search]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const payload = { ...data };

      if (selectedProject) {
        await updateProject(selectedProject.project_id, payload);
      }
      else {
        await createProject(payload);
      }

      setDialogOpen(false);
      setSelectedProject(null);

      await loadProjects();

      setSnackbar({
        open: true,
        message: "Project saved successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Save Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Operation failed",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteProject(selectedProject.project_id);
      await loadProjects();

      setDeleteOpen(false);
      setSelectedProject(null);

      setSnackbar({
        open: true,
        message: "Project deleted successfully",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Delete Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Delete failed",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePublish = async (project) => {
    if (isPublishing) {
      return;
    }

    try {
      setIsPublishing(true);
      await publishProject(project.project_id);
      await loadProjects();

      setSnackbar({
        open: true,
        message: "Project published and assigned users notified",
        severity: "success"
      });
    }
    catch (error) {
      console.error("Project Publish Error:", error.response?.data || error);
      setSnackbar({
        open: true,
        message: "Publish failed",
        severity: "error"
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        p: 3
      }}
    >
      <PageHeader
        title="Project Planning"
        subtitle="Draft projects awaiting publish. Publish to move a project to the Projects page and notify assigned users."
        actions={canManageProjects && (
          <AppButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedProject(null);
              setDialogOpen(true);
            }}
          >
            Create Project
          </AppButton>
        )}
      />

      <Box
        sx={{
          mb: 2,
          maxWidth: 320
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search draft projects"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      <ProjectTable
        projects={filteredProjects}
        loading={loading}
        readOnly={!canManageProjects}
        selectable={false}
        onEdit={(project) => {
          setSelectedProject(project);
          setDialogOpen(true);
        }}
        onDelete={(project) => {
          setSelectedProject(project);
          setDeleteOpen(true);
        }}
        onPublish={handlePublish}
      />

      <ProjectDialog
        open={dialogOpen}
        project={selectedProject}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedProject(null);
        }}
        onSave={handleSave}
      />

      <DeleteDialog
        open={deleteOpen}
        isDeleting={isDeleting}
        title="Delete Project"
        message="Are you sure you want to delete this draft project?"
        itemName={selectedProject?.project_name}
        onClose={() => {
          if (isDeleting) {
            return;
          }
          setDeleteOpen(false);
          setSelectedProject(null);
        }}
        onConfirm={handleDelete}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ProjectPlanning;
